<script setup>
import { ref, onMounted } from 'vue'
import useFilterStore from '@/store/modules/filter'
import usei18nStore from '@/store/modules/i18n'
import useConfigStore from '@/store/modules/config'
import useUIStore from '@/store/modules/ui'
import { useRouter } from 'vue-router'

const router = useRouter()

const configStore = useConfigStore()
const filterStore = useFilterStore()
const i18nStore = usei18nStore()
const uiStore = useUIStore()

const initialised = ref(false)

const initialiseApp = () => {
  const calls = [
    configStore.fetchConfig(),
    i18nStore.fetchLocale('en'),
    uiStore.loadDefaultValues(),
    configStore.fetchPermissions(),
  ]

  Promise.all(calls)
    .then(() => {
      filterStore.overwriteFilterOptions(
        configStore.getConfigByKey('application-config.filtersList') || [],
      )
      filterStore.overwriteFilterGroups(
        configStore.getConfigByKey('application-config.filterGroups') || [],
      )

      initialised.value = true
    })
    .catch(async (error) => {
      console.error(error)
      // Wait for the navigation to actually land on /401 before allowing <router-view> to
      // render - otherwise it can race vue-router's own initial navigation to "/" and render
      // whatever route wins that race (e.g. the timeline), rather than the error page.
      await router.push({ name: '401' })
      initialised.value = true
    })
}

/**
 * Embedded applications will receive a message from the parent application with the app params
 */
const handleEvent = (e) => {
  if (e.origin === window.location.origin) {
    return
  }

  if (e.data.name === 'surveyor-pathway-changed') {
    // Set the app params in the config store
    configStore.setAppParams(e.data.params)

    // Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' })

    if (!initialised.value) {
      initialiseApp()
    }
  }

  if (e.data.name === 'darkmode-changed') {
    const body = document.documentElement
    if (e.data.darkMode) {
      body.classList.add('is-dark')
    } else {
      body.classList.remove('is-dark')
    }

    uiStore.state.isDark = e.data.darkMode
  }
}

// Register the event listener for embedded applications
window.addEventListener('message', handleEvent)

onMounted(() => {
  window.parent.postMessage({ name: 'surveyor-app-loaded' }, '*')
})

/**
 * If the application is not embedded, set the app params from the query string
 */
const urlParams = new URLSearchParams(window.location.search)
const appParams = {}
for (const [key, value] of urlParams.entries()) {
  appParams[key] = value
}
if (appParams.embedded !== 'true') {
  configStore.setAppParams(appParams)
  initialiseApp()
}

// const loading = ref(false)
</script>

<template>
  <div id="root" class="gx-app-layout ant-layout ant-layout-has-sider">
    <!-- Overrides the default colour for ant design -->
    <a-config-provider
      :theme="{
        token: {
          colorPrimary: '#072942',
        },
      }"
    >
      <router-view v-if="initialised" />
    </a-config-provider>
  </div>
</template>

<style>
body {
  margin: 0px;
  min-height: 100vh;
  padding-bottom: 12px !important;
}
</style>
